import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import styled, { keyframes } from "styled-components"
import { MobergFontFamily } from "../MobergFont/MobergFont"
import { MdClose, MdContentCopy, MdCheck } from "react-icons/md"
import { LuFileText, LuMessageSquare, LuShieldCheck, LuSparkles } from "react-icons/lu"
import claraLogo from "../../assets/images/clara-logo.png"

/* What Clara is, for someone meeting her at a bedside.
 *
 * Four sections behind a nav rather than one long scroll: a clinician looking up what she
 * will not answer should not have to pass everything else to reach it. Written from what
 * the system actually does, and the list of what she refuses is as load bearing as the
 * list of what she does, because someone who does not know where the edge is cannot tell
 * a refusal from a failure.
 */

const SIGNALS = [
	["Heart rate", "bpm"],
	["SpO2", "%"],
	["ICP", "mmHg"],
	["Mean arterial pressure", "mmHg"],
	["Systolic blood pressure", "mmHg"],
	["Diastolic blood pressure", "mmHg"],
	["Respiratory rate", "breaths/min"],
	["EtCO2", "mmHg"],
]

const CAN = [
	["Time above or below a value", "How long was the heart rate over 120 in the past 4 hours?"],
	["Time inside or outside a range", "How long was MAP between 70 and 100?"],
	["A summary of a window", "What was the average heart rate? What was the highest?"],
	["A count, or a share of the window", "How many times did SpO2 drop below 90?"],
	["A reviewed clinical check", "Is the patient hypercapnic? Does the patient have raised ICP?"],
	["A clock time or a date", "What was the ICP between 10:45 and 11:30?"],
	["Your own policies and guidelines", "Who may inject chemotherapy into an Ommaya reservoir?"],
	["What she can see for this patient", "What signals do you have for this patient?"],
]

const EXAMPLES = [
	["Vitals", [
		"What was the average heart rate over the past 6 hours?",
		"How many minutes was heart rate over 120 in the past 4 hours?",
		"How long was SpO2 below 90 in the past 4 hours?",
		"How long was MAP between 70 and 100 in the past 6 hours?",
		"What was the ICP between 10:45 and 11:30?",
	]],
	["Clinical checks", [
		"Is the patient hypercapnic?",
		"Is the patient hypotensive?",
		"Is the patient tachycardic?",
		"Does the patient have raised ICP?",
	]],
	["This patient, and your documents", [
		"What signals do you have for this patient?",
		"Who is allowed to inject chemotherapy into an Ommaya reservoir?",
	]],
]

const WILL_NOT = [
	["Give clinical advice",
	 "She will not tell you what to do, what will happen, or whether a number is acceptable. She reports what was measured and what your documents say."],
	["Answer drug questions",
	 "Doses, compatibility, concentrations and side effects are in nothing she reads, so she says so rather than guessing."],
	["Invent a threshold",
	 "A clinical check runs against a reviewed definition. Ask about a condition she holds none for and she will say so, and offer to measure a value you give her."],
	["Answer for a different day",
	 "A date she cannot read is refused rather than quietly replaced with a window she can."],
	["Read two signals at once",
	 "One signal per question. Two questions is how you compare two signals."],
	["Watch, or raise an alarm",
	 "She answers when asked. She is not a monitor."],
	["Learn from your chats",
	 "Nothing you ask changes how she answers later. Her documents and her thresholds change only when someone changes them."],
]

const SECTIONS = [
	{ id: "how", label: "How she works", icon: LuShieldCheck },
	{ id: "ask", label: "What to ask", icon: LuMessageSquare },
	{ id: "try", label: "Questions to try", icon: LuSparkles },
	{ id: "limits", label: "Where she stops", icon: LuFileText },
]

function CopyRow({ text }) {
	const [copied, setCopied] = useState(false)
	const copy = () => {
		if (!navigator.clipboard) {
			return
		}
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 1400)
		})
	}
	return (
		<Copyable type="button" onClick={copy} $copied={copied}>
			<CopyText>{text}</CopyText>
			<CopyMark $copied={copied} aria-hidden="true">
				{copied ? <MdCheck size={14} /> : <MdContentCopy size={13} />}
			</CopyMark>
		</Copyable>
	)
}

export function ClaraGuide({ open, onClose }) {
	const [section, setSection] = useState("how")

	useEffect(() => {
		if (!open) {
			return undefined
		}
		const onKeyDown = event => {
			if (event.key === "Escape") {
				onClose?.()
			}
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [open, onClose])

	if (!open) {
		return null
	}

	/* Rendered into the body, not where it is written. The rail sets position relative
	   with a z-index, which makes it a stacking context, and a fixed overlay inside one
	   can never paint above anything outside it however high its own z-index goes. */
	return createPortal(
		<Overlay onClick={onClose}>
			<Sheet onClick={event => event.stopPropagation()} role="dialog" aria-label="How Clara works">
				<Head>
					<Heading>
						{/* Her own mark, not a stand in for one. */}
						<Mark src={claraLogo} alt="" aria-hidden="true" />
						<div>
							<Title>Clara</Title>
							<Sub>What she reads, what she is held to, and where she stops.</Sub>
						</div>
					</Heading>
					<Close type="button" onClick={onClose} aria-label="Close">
						<MdClose size={19} />
					</Close>
				</Head>

				<Split>
					<Nav>
						{SECTIONS.map(({ id, label, icon: Icon }) => (
							<NavItem
								key={id}
								type="button"
								$active={section === id}
								onClick={() => setSection(id)}
							>
								<Icon size={15} />
								<span>{label}</span>
							</NavItem>
						))}
					</Nav>

					<Body>
						{section === "how" && (
							<>
								<Lede>
									Ask about the patient in front of you, or about your own policies, in the words
									you would use out loud.
								</Lede>

								<Block>
									<H>She answers from two places</H>
									<Pair>
										<Card>
											<CardTitle>This patient&rsquo;s recording</CardTitle>
											<CardText>
												Every number is computed by the platform from the archive itself, the same
												data Data Review draws. Readings outside a signal&rsquo;s physiologic range
												are excluded as monitor artifact, and she says how many.
											</CardText>
										</Card>
										<Card>
											<CardTitle>Your uploaded documents</CardTitle>
											<CardText>
												Policies, procedures and references uploaded for your site. Answers quote
												the passage and cite the page, and the passage is shown beside the answer.
											</CardText>
										</Card>
									</Pair>
								</Block>

								<Block>
									<H>Two rules she is held to</H>
									<Rule>
										<Tick aria-hidden="true"><MdCheck size={13} /></Tick>
										<div>
											<RuleName>Every number was computed, not written</RuleName>
											<RuleText>
												The model chooses how to phrase an answer. It never calculates one. The
												phrasing is checked against the computed sentence, and if a single number
												differs it is thrown away and the computed sentence is shown instead.
											</RuleText>
										</div>
									</Rule>
									<Rule>
										<Tick aria-hidden="true"><MdCheck size={13} /></Tick>
										<div>
											<RuleName>Every quoted rule is really in the document</RuleName>
											<RuleText>
												A statement from your documents survives only if a word for word quote from
												a retrieved passage backs it. Anything she cannot back that way is dropped
												before you see it, even when it sounds right.
											</RuleText>
										</div>
									</Rule>
								</Block>
							</>
						)}

						{section === "ask" && (
							<>
								<Lede>Eight shapes of question, and the signals they can be asked about.</Lede>
								<Block>
									<List>
										{CAN.map(([what, example]) => (
											<Item key={what}>
												<ItemName>{what}</ItemName>
												<Example>{example}</Example>
											</Item>
										))}
									</List>
								</Block>
								<Block>
									<H>Signals she reads</H>
									<Chips>
										{SIGNALS.map(([name, unit]) => (
											<Chip key={name}>
												{name} <Unit>{unit}</Unit>
											</Chip>
										))}
									</Chips>
									<Note>
										Not every recording holds all of them. Ask what she can see for this patient and
										she will list what is in that archive and what is missing. Blood pressure is read
										from the arterial line only, never from a cuff, because those are different
										measurements.
									</Note>
								</Block>
							</>
						)}

						{section === "try" && (
							<>
								<Lede>Click a question to copy it, then paste it into the message box.</Lede>
								{EXAMPLES.map(([group, questions]) => (
									<Block key={group}>
										<H>{group}</H>
										<Examples>
											{questions.map(question => (
												<CopyRow key={question} text={question} />
											))}
										</Examples>
									</Block>
								))}
							</>
						)}

						{section === "limits" && (
							<>
								<Lede>
									Knowing where she stops is what lets you read a refusal as an answer rather than a
									fault.
								</Lede>
								<Block>
									<List>
										{WILL_NOT.map(([what, why]) => (
											<Item key={what}>
												<ItemName>{what}</ItemName>
												<Example as="p">{why}</Example>
											</Item>
										))}
									</List>
								</Block>
								<Block>
									<H>Worth knowing</H>
									<Note>
										A chat is about one patient, so a transcript never changes patient halfway
										through. Choosing a different one starts a new chat. A chat with no patient is
										fine, and is how you ask about your documents. Times like
										&ldquo;yesterday&rdquo; are measured from the end of the recording rather than
										from today, so on an archive from 2017 yesterday is a day in 2017, and she says
										which one.
									</Note>
								</Block>
							</>
						)}
					</Body>
				</Split>

				<Foot>
					Clara is in beta. She can be wrong, and she can be incomplete. Verify against the
					patient&rsquo;s data before making any clinical decision.
				</Foot>
			</Sheet>
		</Overlay>,
		document.body
	)
}

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`

const riseIn = keyframes`
	from { opacity: 0; transform: translateY(10px) scale(0.994); }
	to   { opacity: 1; transform: translateY(0) scale(1); }
`

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 10050;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 32px;
	background: rgba(12, 18, 28, 0.5);
	backdrop-filter: blur(3px);
	animation: ${fadeIn} 140ms ease;
`

const Sheet = styled.div`
	display: flex;
	flex-direction: column;
	width: min(860px, 100%);
	height: min(620px, 100%);
	border-radius: 16px;
	background: var(--color-surface-panel, #fff);
	box-shadow: 0 28px 70px rgba(16, 24, 40, 0.3);
	overflow: hidden;
	animation: ${riseIn} 170ms cubic-bezier(0.23, 1, 0.32, 1) both;
`

const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 18px;
	border-bottom: 1px solid var(--color-border-light);
`

const Heading = styled.div`
	display: flex;
	align-items: center;
	gap: 11px;
	min-width: 0;
`

const Mark = styled.img`
	flex-shrink: 0;
	width: 30px;
	height: 30px;
	object-fit: contain;
	user-select: none;
`

const Title = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 16px;
	font-weight: 650;
	letter-spacing: -0.01em;
	color: var(--text-color);
	line-height: 1.2;
`

const Sub = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--text-secondary-color);
`

const Close = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 8px;
	background: transparent;
	color: var(--text-secondary-color);
	cursor: pointer;
	transition: background-color 120ms ease, color 120ms ease;

	&:hover {
		background: var(--color-hover-neutral);
		color: var(--text-color);
	}
`

const Split = styled.div`
	flex: 1;
	min-height: 0;
	display: grid;
	grid-template-columns: 196px 1fr;

	@media (max-width: 700px) {
		grid-template-columns: 1fr;
	}
`

const Nav = styled.nav`
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 12px 10px;
	border-right: 1px solid var(--color-border-light);
	overflow-y: auto;

	@media (max-width: 700px) {
		flex-direction: row;
		border-right: none;
		border-bottom: 1px solid var(--color-border-light);
		overflow-x: auto;
	}
`

const NavItem = styled.button`
	display: flex;
	align-items: center;
	gap: 9px;
	width: 100%;
	padding: 8px 10px;
	border: none;
	border-radius: 9px;
	text-align: left;
	white-space: nowrap;
	cursor: pointer;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: ${p => (p.$active ? 600 : 450)};
	background: ${p => (p.$active ? "rgba(32, 125, 234, 0.1)" : "transparent")};
	color: ${p => (p.$active ? "var(--color-accent-interactive)" : "var(--text-secondary-color)")};
	transition: background-color 120ms ease, color 120ms ease;

	&:hover {
		background: ${p => (p.$active ? "rgba(32, 125, 234, 0.13)" : "var(--color-hover-neutral)")};
		color: ${p => (p.$active ? "var(--color-accent-interactive)" : "var(--text-color)")};
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: -2px;
	}
`

const Body = styled.div`
	min-height: 0;
	overflow-y: auto;
	padding: 20px 24px 24px;
	display: flex;
	flex-direction: column;
	gap: 20px;
	animation: ${fadeIn} 160ms ease;
`

const Lede = styled.p`
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13.5px;
	line-height: 1.6;
	color: var(--text-color);
`

const Block = styled.section`
	display: flex;
	flex-direction: column;
	gap: 10px;
`

const H = styled.h3`
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	font-weight: 650;
	letter-spacing: 0.07em;
	text-transform: uppercase;
	color: var(--text-secondary-color);
`

const Pair = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;

	@media (max-width: 620px) {
		grid-template-columns: 1fr;
	}
`

const Card = styled.div`
	padding: 13px 14px;
	border-radius: 11px;
	border: 1px solid var(--color-border-light);
`

const CardTitle = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 650;
	color: var(--text-color);
	margin-bottom: 5px;
`

const CardText = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.55;
	color: var(--text-secondary-color);
`

const Rule = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 10px;
`

const Tick = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	margin-top: 1px;
	border-radius: 50%;
	background: rgba(32, 125, 234, 0.12);
	color: var(--color-accent-interactive, #207dea);
`

const RuleName = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 650;
	color: var(--text-color);
	margin-bottom: 2px;
`

const RuleText = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.55;
	color: var(--text-secondary-color);
`

const List = styled.div`
	display: flex;
	flex-direction: column;
`

const Item = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 10px 0;

	& + & {
		border-top: 1px solid var(--color-border-light);
	}

	&:first-child {
		padding-top: 0;
	}
`

const ItemName = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 600;
	color: var(--text-color);
`

const Example = styled.div`
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.55;
	color: var(--text-secondary-color);
`

const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
`

const Chip = styled.span`
	padding: 5px 11px;
	border-radius: 999px;
	border: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--text-color);
`

const Unit = styled.span`
	color: var(--text-secondary-color);
	font-size: 11.5px;
`

const Note = styled.p`
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.6;
	color: var(--text-secondary-color);
`

const Examples = styled.div`
	display: flex;
	flex-direction: column;
	gap: 5px;
`

const CopyMark = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 26px;
	height: 26px;
	border-radius: 7px;
	color: ${p => (p.$copied ? "var(--color-accent-interactive)" : "var(--text-secondary-color)")};
	opacity: ${p => (p.$copied ? 1 : 0)};
	transition: opacity 120ms ease, color 120ms ease;
`

const Copyable = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 9px 8px 9px 12px;
	border: 1px solid ${p => (p.$copied ? "var(--color-accent-interactive)" : "var(--color-border-light)")};
	border-radius: 10px;
	background: transparent;
	text-align: left;
	cursor: pointer;
	transition: background-color 120ms ease, border-color 120ms ease;

	&:hover,
	&:focus-visible {
		background: rgba(32, 125, 234, 0.07);
		border-color: var(--color-accent-interactive);
	}

	&:hover ${CopyMark},
	&:focus-visible ${CopyMark} {
		opacity: 1;
	}

	&:focus-visible {
		outline: none;
	}
`

const CopyText = styled.span`
	flex: 1;
	min-width: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--text-color);
`

const Foot = styled.div`
	flex-shrink: 0;
	padding: 12px 18px;
	border-top: 1px solid var(--color-border-light);
	background: var(--color-hover-neutral);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	line-height: 1.5;
	color: var(--text-secondary-color);
`
